import React, { useState, Fragment, useEffect, useMemo, useRef } from 'react'

import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/solid'
import classNames from 'classnames'

export interface Option {
	option: string
	value: string | number
	disabled?: boolean
	id?: string
}

interface SelectInputProps {
	options: Array<Option>
	btnClass?: string
	onChange: (option: Option) => void
	isValid?: boolean
	selectedValue?: string | number
	absolute?: boolean
	multiline?: boolean
	isSearchable?: boolean
}

const styles = {
	container: 'relative',
	listboxButton:
		'bg-white relative w-full border rounded-md shadow-3 text-left cursor-pointer text-sm flex items-center',
	default: 'border-gray-300',
	valid: 'border-green',
	invalid: 'border-red',
	listboxOption:
		'z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm',

	spanOption: 'block pr-6',

	iconGroup:
		'absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none',
	icon: 'h-5 w-5 text-gray-400',
	activeGroup: 'cursor-pointer select-none relative py-2 pl-3 pr-9',
	active: 'text-white bg-primary',
	inactive: 'text-gray-900',
	disabledLabel: 'text-gray font-medium',
	disabled: 'text-gray-300 cursor-default',

	selectedGroup: 'block',

	selected: 'font-semibold',
	notSelected: 'font-normal',
	activeSelectedGroup: 'absolute inset-y-0 right-0 flex items-center pr-4',
	activeSelected: 'text-white',
	inactiveNotSelected: 'text-primary',
	checkIcon: 'h-5 w-5',

	searchContainer: 'sticky top-0 z-20 bg-white p-2 border-b border-gray-100',
	searchInput:
		'w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary',
	noResult: 'py-2 px-3 text-gray-500 text-sm italic',
}

export default function SelectInput({
	options,
	btnClass = 'px-[13px] py-[6.5px]',
	onChange,
	isValid,
	selectedValue,
	absolute = true,
	multiline = false,
	isSearchable = false,
}: SelectInputProps) {
	const [selected, setSelected] = useState(options[0])
	const [query, setQuery] = useState('')

	/*
	 * O último valor que veio de fora e foi obedecido.
	 *
	 * Ele separa as duas coisas que chegam pelo mesmo caminho: um re-render do
	 * pai com o valor de sempre, que não pode atropelar a escolha que o usuário
	 * acabou de fazer, e um valor NOVO, que manda. Um sinalizador de "já
	 * escolheu à mão" não distingue os dois — desligada a sincronização, o
	 * campo passa a mostrar a escolha anterior enquanto o resto da tela já
	 * mudou de registro, e a tela de revisão de questões, que reaproveita o
	 * componente ao avançar de questão, exibia o gabarito da questão anterior
	 * com a fundamentação da seguinte ao lado.
	 */
	const obeyedValue = useRef<string | number | undefined>(undefined)

	useEffect(() => {
		if (typeof selectedValue === 'undefined') return
		if (obeyedValue.current === selectedValue) return

		const value = options.find((option) => option.value === selectedValue)

		if (!value) return

		obeyedValue.current = selectedValue
		setSelected(value)
	}, [selectedValue, options])

	const filteredOptions = useMemo(() => {
		if (!isSearchable || query === '') return options

		return options.filter((opt) =>
			opt.option.toLowerCase().includes(query.toLowerCase())
		)
	}, [options, query, isSearchable])

	const textBehaviorClass = multiline
		? 'whitespace-normal break-words'
		: 'truncate'

	return (
		<Listbox
			value={selected}
			onChange={(value) => {
				onChange(value)
				setSelected(value)
				setQuery('')
			}}
		>
			{({ open }) => (
				<>
					<div className={styles.container}>
						<Listbox.Button
							className={`${styles.listboxButton} ${(() => {
								if (typeof isValid === 'undefined') return styles.default
								if (!isValid) return styles.invalid

								return styles.valid
							})()} ${btnClass}`}
						>
							<span
								className={`${styles.spanOption} ${
									selected?.disabled ? styles.disabledLabel : ''
								} ${textBehaviorClass}`}
							>
								{selected?.option || 'Selecione'}
							</span>
							<span className={styles.iconGroup}>
								<ChevronDownIcon className={styles.icon} aria-hidden='true' />
							</span>
						</Listbox.Button>

						<Transition
							show={open}
							as={Fragment}
							leave='transition ease-in duration-100'
							leaveFrom='opacity-100'
							leaveTo='opacity-0'
							afterLeave={() => setQuery('')}
						>
							<Listbox.Options
								className={`${absolute ? 'absolute' : 'relative'} ${
									styles.listboxOption
								} scroll_`}
							>
								{isSearchable && (
									<div className={styles.searchContainer}>
										<input
											type='text'
											className={styles.searchInput}
											placeholder='Buscar...'
											value={query}
											onChange={(e) => setQuery(e.target.value)}
											onKeyDown={(e) => e.stopPropagation()}
											onClick={(e) => e.stopPropagation()}
										/>
									</div>
								)}

								{filteredOptions.length === 0 ? (
									<div className={styles.noResult}>
										Nenhum resultado encontrado
									</div>
								) : (
									filteredOptions.map((option) => (
										<Listbox.Option
											key={option.value}
											className={({ active, disabled }) =>
												classNames(
													`${styles.activeGroup} ${
														active && !disabled
															? styles.active
															: styles.inactive
													}`
												)
											}
											value={option}
											disabled={option.disabled}
										>
											{({ selected: selectedItem, active, disabled }) => (
												<>
													<span
														className={classNames(
															`${styles.selectedGroup} ${
																selectedItem && !disabled
																	? styles.selected
																	: styles.notSelected
															} ${
																disabled ? styles.disabled : ''
															} ${textBehaviorClass}`
														)}
													>
														{option.option}
													</span>

													{selectedItem && !disabled ? (
														<span
															className={classNames(
																`${styles.activeSelectedGroup} ${
																	active
																		? styles.activeSelected
																		: styles.inactiveNotSelected
																}`
															)}
														>
															<CheckIcon
																className={styles.checkIcon}
																aria-hidden='true'
															/>
														</span>
													) : null}
												</>
											)}
										</Listbox.Option>
									))
								)}
							</Listbox.Options>
						</Transition>
					</div>
				</>
			)}
		</Listbox>
	)
}
